import dayjs from "dayjs";

export const LEASE_EXTRACTION_VERSION = "assistant-lease-v3";
export const LEASE_CONFIDENCE_THRESHOLD = 0.85;

export const LEASE_REQUIRED_FIELD_KEYS = Object.freeze([
  "tenantFullName",
  "suiteNumber",
  "rentAmount",
  "startDate",
]);

const DATE_FORMAT_CANDIDATES = Object.freeze([
  "YYYY-MM-DD",
  "YYYY/MM/DD",
  "MM/DD/YYYY",
  "DD/MM/YYYY",
  "MMM D, YYYY",
  "MMMM D, YYYY",
  "D MMM YYYY",
  "D MMMM YYYY",
]);

const SUITE_STOPWORDS = new Set([
  "a",
  "and",
  "at",
  "address",
  "apartment",
  "condominium",
  "form",
  "is",
  "location",
  "landlord",
  "none",
  "no",
  "of",
  "premises",
  "rental",
  "suite",
  "tenant",
  "the",
  "unit",
  "yes",
]);

const NAME_NOISE_PHRASES = Object.freeze([
  "tenancy agreement",
  "standard residential tenancy",
  "this form",
  "residential tenancies",
  "tenant responsibilities",
  "ending the tenancy",
  "name of tenant",
  "form 1",
  "rental unit",
  "landlord",
]);

const LEASE_FIELD_DEFS = Object.freeze({
  tenantFullName: {
    label: "Tenant Full Name",
    constraints: "Full legal tenant name.",
    keywords: ["tenant", "lessee", "occupant", "resident"],
    patterns: [
      /(?:tenant(?:\(s\))?|lessee(?:s)?|occupant(?:s)?|resident(?:s)?)\s*(?:name|names)?\s*[:\-]?\s*([^\n]{3,140})/i,
      /name(?:s)?\s+of\s+tenant(?:s)?\s*[:\-]?\s*([^\n]{3,140})/i,
    ],
    sanitizer: sanitizeName,
  },
  tenantEmail: {
    label: "Tenant Email",
    constraints: "Valid email format.",
    keywords: ["tenant", "email", "resident"],
    patterns: [
      /([A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,})/i,
    ],
    sanitizer: sanitizeEmail,
  },
  tenantPhone: {
    label: "Tenant Phone",
    constraints: "Phone number in local format.",
    keywords: ["tenant", "phone", "mobile", "contact"],
    patterns: [
      /(?:tenant\s+phone|phone\s+number|contact\s+number)\s*[:\-]?\s*([+()0-9\-\s]{7,25})/i,
      /([+]?[0-9][0-9()\-\s]{7,20}[0-9])/i,
    ],
    sanitizer: sanitizePhone,
  },
  propertyAddress: {
    label: "Property Address",
    constraints: "Rental property street address.",
    keywords: ["rental unit", "premises", "address", "property"],
    patterns: [
      /(?:rental\s+unit|premises|property\s+address|address\s+of\s+rental\s+unit)\s*[:\-]?\s*([^\n]{6,180})/i,
    ],
    sanitizer: sanitizeAddress,
  },
  suiteNumber: {
    label: "Suite / Unit",
    constraints: "Unit, suite, or apartment identifier.",
    keywords: ["suite", "unit", "apartment", "apt"],
    patterns: [
      /(?:suite|unit|apartment|apt\.?)(?:\s+number|\s+no\.?|\s*#)?\s*[:\-]?\s*([A-Za-z0-9\-]+)/i,
      /rental\s+unit\s*[:\-]?\s*([A-Za-z0-9\-]+)/i,
    ],
    sanitizer: sanitizeSuite,
  },
  rentAmount: {
    label: "Base Rent",
    constraints: "Monthly rent amount in currency.",
    keywords: ["rent", "monthly", "lawful", "payable"],
    patterns: [
      /(?:base\s+rent|monthly\s+rent|lawful\s+rent|rent\s+payable|rent\s+amount)\s*[:\-]?\s*\$?\s*([0-9][0-9,]*(?:\.[0-9]{1,2})?)/i,
      /\$\s*([0-9][0-9,]*(?:\.[0-9]{1,2})?)\s*(?:\/\s*month|monthly|per\s+month)/i,
    ],
    sanitizer: sanitizeMoney,
  },
  rentFrequency: {
    label: "Rent Frequency",
    constraints: "One of monthly, weekly, yearly.",
    keywords: ["monthly", "weekly", "yearly", "frequency"],
    patterns: [
      /(?:rent\s+frequency|frequency)\s*[:\-]?\s*(monthly|weekly|yearly|annual)/i,
      /(monthly|weekly|yearly|annual)\s+rent/i,
    ],
    sanitizer: sanitizeFrequency,
  },
  startDate: {
    label: "Start Date",
    constraints: "Date in YYYY-MM-DD.",
    keywords: ["start date", "commencement", "move-in", "from"],
    patterns: [
      /(?:start\s+date|commencement\s+date|move[- ]in\s+date|lease\s+start|term\s+starts?)\s*[:\-]?\s*([^\n]{6,50})/i,
      /from\s+([^\n]{6,40})\s+(?:to|until|through)/i,
    ],
    sanitizer: sanitizeDate,
  },
  endDate: {
    label: "End Date",
    constraints: "Date in YYYY-MM-DD if fixed term.",
    keywords: ["end date", "expiry", "to", "until", "through"],
    patterns: [
      /(?:end\s+date|lease\s+end|expiry\s+date|term\s+ends?|fixed\s+term\s+end)\s*[:\-]?\s*([^\n]{6,50})/i,
      /(?:to|until|through)\s+([^\n]{6,40})/i,
    ],
    sanitizer: sanitizeDate,
  },
  securityDeposit: {
    label: "Security Deposit",
    constraints: "Deposit amount in currency.",
    keywords: ["security deposit", "damage deposit", "rent deposit"],
    patterns: [
      /(?:security\s+deposit|damage\s+deposit|rent\s+deposit|deposit\s+amount)\s*[:\-]?\s*\$?\s*([0-9][0-9,]*(?:\.[0-9]{1,2})?)/i,
    ],
    sanitizer: sanitizeMoney,
  },
  utilities: {
    label: "Utilities",
    constraints: "Short summary of included/excluded utilities.",
    keywords: ["utilities", "electricity", "water", "heat", "included"],
    patterns: [
      /(?:utilities|services|included\s+utilities)\s*[:\-]?\s*([^\n]{4,200})/i,
      /(?:electricity|water|heat|gas)[^\n]{0,120}(?:included|not\s+included|tenant|landlord)/i,
    ],
    sanitizer: sanitizeSentence,
  },
  parking: {
    label: "Parking",
    constraints: "Parking details or none.",
    keywords: ["parking", "stall", "space"],
    patterns: [
      /(?:parking|parking\s+stall|parking\s+space)\s*[:\-]?\s*([^\n]{2,160})/i,
    ],
    sanitizer: sanitizeSentence,
  },
  pets: {
    label: "Pets",
    constraints: "Pet policy summary.",
    keywords: ["pets", "pet", "animal"],
    patterns: [
      /(?:pets?|animals?)\s*[:\-]?\s*([^\n]{2,160})/i,
    ],
    sanitizer: sanitizeSentence,
  },
  smoking: {
    label: "Smoking",
    constraints: "Smoking policy summary.",
    keywords: ["smoking", "smoke"],
    patterns: [
      /(?:smoking|smoke[- ]free)\s*[:\-]?\s*([^\n]{2,160})/i,
    ],
    sanitizer: sanitizeSentence,
  },
  signatories: {
    label: "Signatories",
    constraints: "Names of signing parties.",
    keywords: ["signature", "signed", "landlord", "tenant"],
    patterns: [
      /(?:tenant\s+signature(?:\(s\))?|signed\s+by\s+tenant)\s*[:\-]?\s*([^\n]{3,160})/i,
      /(?:landlord\s+signature|signed\s+by\s+landlord)\s*[:\-]?\s*([^\n]{3,160})/i,
    ],
    sanitizer: sanitizeSentence,
  },
});

function safeString(value) {
  return String(value == null ? "" : value).trim();
}

function normalizeWhitespace(value) {
  return safeString(value).replace(/\s+/g, " ").trim();
}

function normalizePageTextWithLineBreaks(value) {
  return String(value || "")
    .split(/\n+/)
    .map((line) => normalizeWhitespace(line))
    .filter(Boolean)
    .join("\n");
}

function sanitizeName(value) {
  const clean = normalizeWhitespace(value).replace(/[;:,]+$/g, "");
  if (!clean) return "";
  if (/\d/.test(clean)) return "";
  const lowered = clean.toLowerCase();
  for (let i = 0; i < NAME_NOISE_PHRASES.length; i += 1) {
    if (lowered.includes(NAME_NOISE_PHRASES[i])) return "";
  }
  const words = clean
    .replace(/[^a-zA-Z\s.'-]/g, " ")
    .split(/\s+/)
    .filter(Boolean);
  if (words.length < 2 || words.length > 6) return "";
  if (clean.length > 90) return "";
  return clean;
}

function sanitizeEmail(value) {
  const clean = normalizeWhitespace(value).toLowerCase();
  const match = clean.match(/[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/i);
  return match ? match[0] : "";
}

function sanitizePhone(value) {
  const clean = normalizeWhitespace(value);
  if (!clean) return "";
  const digits = clean.replace(/[^0-9]/g, "");
  if (digits.length < 7) return "";
  return clean;
}

function sanitizeAddress(value) {
  const clean = normalizeWhitespace(value).replace(/[;:,]+$/g, "");
  if (!clean || clean.length < 6) return "";
  return clean;
}

function sanitizeSuite(value) {
  const clean = normalizeWhitespace(value)
    .replace(/^(suite|unit|apartment|apt\.?)\s*/i, "")
    .replace(/^#/, "")
    .replace(/[.,;:]+$/g, "")
    .trim();
  if (!clean) return "";
  const compact = clean.replace(/\s+/g, "");
  const lowered = compact.toLowerCase();
  if (SUITE_STOPWORDS.has(lowered)) return "";
  if (!/^[A-Za-z0-9-]{1,12}$/.test(compact)) return "";
  if (/^[A-Za-z]+$/.test(compact) && compact.length > 2) return "";
  return clean;
}

function sanitizeMoney(value) {
  const raw = safeString(value);
  if (!raw) return "";
  const cleaned = raw.replace(/[^0-9.,-]/g, "");
  if (!cleaned) return "";
  const normalized = cleaned.includes(",") && !cleaned.includes(".")
    ? cleaned.replace(/,/g, ".")
    : cleaned.replace(/,/g, "");
  const amount = Number(normalized);
  if (!Number.isFinite(amount) || amount <= 0) return "";
  return amount.toFixed(2);
}

function sanitizeFrequency(value) {
  const clean = normalizeWhitespace(value).toLowerCase();
  if (!clean) return "";
  if (clean.includes("month")) return "monthly";
  if (clean.includes("week")) return "weekly";
  if (clean.includes("year") || clean.includes("annual")) return "yearly";
  return "";
}

function sanitizeDate(value) {
  const clean = normalizeWhitespace(value);
  if (!clean) return "";
  for (let i = 0; i < DATE_FORMAT_CANDIDATES.length; i += 1) {
    const parsed = dayjs(clean, DATE_FORMAT_CANDIDATES[i], true);
    if (parsed.isValid()) return parsed.format("YYYY-MM-DD");
  }
  const fallback = dayjs(clean);
  if (fallback.isValid()) return fallback.format("YYYY-MM-DD");
  return "";
}

function sanitizeSentence(value) {
  const clean = normalizeWhitespace(value).replace(/[;:,]+$/g, "");
  return clean.slice(0, 240);
}

function splitIntoPages(rawText) {
  const source = String(rawText || "");
  const pages = [];
  const pattern = /(?:^|\n)Page\s+(\d+)\s*\n/gi;
  let match;
  let lastIndex = 0;
  let lastPage = 1;

  while ((match = pattern.exec(source)) !== null) {
    const startIndex = match.index;
    if (startIndex > lastIndex) {
      const chunk = source.slice(lastIndex, startIndex).trim();
      if (chunk) {
        pages.push({ page: lastPage, text: chunk });
      }
    }
    lastIndex = pattern.lastIndex;
    lastPage = Number(match[1] || pages.length + 1);
  }

  const tail = source.slice(lastIndex).trim();
  if (tail) {
    pages.push({ page: lastPage || pages.length + 1, text: tail });
  }

  if (!pages.length) {
    return [{ page: 1, text: source.trim() }];
  }

  return pages;
}

function stripRepeatedHeadersAndFooters(pages) {
  if (!Array.isArray(pages) || pages.length <= 1) return pages || [];
  const headCounts = new Map();
  const tailCounts = new Map();

  pages.forEach((row) => {
    const lines = String(row.text || "")
      .split(/\n+/)
      .map((line) => normalizeWhitespace(line))
      .filter(Boolean);
    lines.slice(0, 2).forEach((line) => {
      if (line.length < 5) return;
      headCounts.set(line, (headCounts.get(line) || 0) + 1);
    });
    lines.slice(-2).forEach((line) => {
      if (line.length < 5) return;
      tailCounts.set(line, (tailCounts.get(line) || 0) + 1);
    });
  });

  const minRepeat = Math.max(2, Math.ceil(pages.length * 0.6));
  const repeatedHead = new Set(
    Array.from(headCounts.entries())
      .filter(([, count]) => count >= minRepeat)
      .map(([line]) => line)
  );
  const repeatedTail = new Set(
    Array.from(tailCounts.entries())
      .filter(([, count]) => count >= minRepeat)
      .map(([line]) => line)
  );

  return pages.map((row) => {
    const lines = String(row.text || "")
      .split(/\n+/)
      .map((line) => normalizeWhitespace(line))
      .filter(Boolean)
      .filter((line, idx, arr) => {
        if (idx < 2 && repeatedHead.has(line)) return false;
        if (idx >= arr.length - 2 && repeatedTail.has(line)) return false;
        return true;
      });
    return {
      page: row.page,
      text: lines.join("\n"),
    };
  });
}

function buildSnippet(text, index, length) {
  const source = String(text || "");
  if (!source) return "";
  const start = Math.max(0, Number(index || 0) - 140);
  const end = Math.min(source.length, Number(index || 0) + Number(length || 0) + 180);
  return normalizeWhitespace(source.slice(start, end));
}

function selectKeywordSnippets(pages, keywords, limit = 3) {
  const out = [];
  const seen = new Set();
  const list = Array.isArray(pages) ? pages : [];
  const normalizedKeywords = (keywords || []).map((word) => safeString(word).toLowerCase()).filter(Boolean);

  for (let p = 0; p < list.length; p += 1) {
    const page = list[p];
    const text = String(page.text || "");
    const lower = text.toLowerCase();

    for (let i = 0; i < normalizedKeywords.length; i += 1) {
      const keyword = normalizedKeywords[i];
      const at = lower.indexOf(keyword);
      if (at < 0) continue;
      const quote = buildSnippet(text, at, keyword.length);
      if (!quote) continue;
      const signature = `${page.page}|${quote}`;
      if (seen.has(signature)) continue;
      seen.add(signature);
      out.push({ page: Number(page.page || 0), quote });
      if (out.length >= limit) return out;
    }
  }

  return out;
}

function findCandidateFromPattern(pages, pattern, sanitizer, confidenceHint) {
  const list = Array.isArray(pages) ? pages : [];
  for (let i = 0; i < list.length; i += 1) {
    const page = list[i];
    const text = String(page.text || "");
    if (!text) continue;

    const regex = new RegExp(pattern.source, pattern.flags);
    const match = regex.exec(text);
    if (!match) continue;

    const rawValue = safeString(match[1] || match[0] || "");
    const cleanValue = sanitizer(rawValue);
    if (!cleanValue) continue;

    const quote = buildSnippet(text, match.index, match[0].length);
    return {
      value: cleanValue,
      confidence: confidenceHint,
      evidence: quote ? [{ page: Number(page.page || 0), quote }] : [],
    };
  }
  return null;
}

function findCandidateFromFormFields(formFields, fieldKey) {
  const entries = Object.entries(formFields || {});
  if (!entries.length) return null;

  const normalizedField = safeString(fieldKey).toLowerCase();
  for (let i = 0; i < entries.length; i += 1) {
    const [rawKey, rawValue] = entries[i];
    const key = safeString(rawKey).toLowerCase();
    if (!key || !key.includes(normalizedField.replace(/[^a-z0-9]/g, ""))) continue;
    const value = sanitizeSentence(rawValue);
    if (!value) continue;
    return {
      value,
      confidence: 0.93,
      evidence: [{ page: 0, quote: `Embedded form field: ${truncate(rawKey, 60)}` }],
    };
  }

  return null;
}

function clampConfidence(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  if (n < 0) return 0;
  if (n > 1) return 1;
  return Number(n.toFixed(3));
}

function truncate(value, maxLen) {
  const clean = safeString(value);
  const limit = Number.isFinite(Number(maxLen)) ? Number(maxLen) : 200;
  return clean.length <= limit ? clean : clean.slice(0, limit);
}

function isIsoDate(value) {
  const v = safeString(value);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return false;
  const parsed = dayjs(v, "YYYY-MM-DD", true);
  return parsed.isValid();
}

function fieldValueLooksValid(fieldKey, value) {
  const v = safeString(value);
  if (!v) return false;

  if (fieldKey === "tenantFullName") {
    const lowered = v.toLowerCase();
    for (let i = 0; i < NAME_NOISE_PHRASES.length; i += 1) {
      if (lowered.includes(NAME_NOISE_PHRASES[i])) return false;
    }
    const words = v
      .replace(/[^a-zA-Z\s.'-]/g, " ")
      .split(/\s+/)
      .filter(Boolean);
    if (words.length < 2 || words.length > 6) return false;
    if (v.length > 90) return false;
    return words.some((word) => word.length >= 2);
  }
  if (fieldKey === "tenantEmail") {
    return /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/i.test(v);
  }
  if (fieldKey === "suiteNumber") {
    const compact = v.replace(/\s+/g, "");
    const lowered = compact.toLowerCase();
    if (!compact || SUITE_STOPWORDS.has(lowered)) return false;
    if (!/^[A-Za-z0-9-]{1,12}$/.test(compact)) return false;
    if (/^[A-Za-z]+$/.test(compact) && compact.length > 2) return false;
    return true;
  }
  if (fieldKey === "rentAmount" || fieldKey === "securityDeposit") {
    const n = Number(v);
    return Number.isFinite(n) && n > 0 && n < 25000;
  }
  if (fieldKey === "startDate" || fieldKey === "endDate") {
    if (!isIsoDate(v)) return false;
    const year = Number(v.slice(0, 4));
    return Number.isFinite(year) && year >= 2000 && year <= 2100;
  }
  return true;
}

function collectFieldProposal({ fieldKey, pages, formFields }) {
  const def = LEASE_FIELD_DEFS[fieldKey];
  if (!def) {
    return {
      value: "",
      confidence: 0,
      evidence: [],
      source: "heuristic",
      rationale: "Unsupported field.",
    };
  }

  const sanitizer = typeof def.sanitizer === "function" ? def.sanitizer : sanitizeSentence;
  const formCandidate = findCandidateFromFormFields(formFields, fieldKey);
  if (formCandidate && formCandidate.value) {
    const normalized = sanitizer(formCandidate.value);
    if (normalized) {
      return {
        value: normalized,
        confidence: clampConfidence(0.95),
        evidence: formCandidate.evidence,
        source: "heuristic",
        rationale: "Matched embedded PDF form field.",
      };
    }
  }

  for (let i = 0; i < def.patterns.length; i += 1) {
    const candidate = findCandidateFromPattern(
      pages,
      def.patterns[i],
      sanitizer,
      i === 0 ? 0.88 : 0.74
    );
    if (!candidate || !candidate.value) continue;
    return {
      value: candidate.value,
      confidence: clampConfidence(candidate.confidence),
      evidence: candidate.evidence,
      source: "heuristic",
      rationale: i === 0 ? "Label-based regex match." : "Heuristic regex match.",
    };
  }

  const fallbackSnippets = selectKeywordSnippets(pages, def.keywords, 1);
  return {
    value: "",
    confidence: 0,
    evidence: fallbackSnippets,
    source: "heuristic",
    rationale: "No confident deterministic match found.",
  };
}

export function buildLeaseRequiredChecklist(fieldMap) {
  const fields = fieldMap && typeof fieldMap === "object" ? fieldMap : {};
  const items = LEASE_REQUIRED_FIELD_KEYS.map((fieldKey) => {
    const row = fields[fieldKey] || {};
    const value = safeString(row.value);
    const confidence = clampConfidence(row.confidence);
    const ready = Boolean(value) && confidence >= LEASE_CONFIDENCE_THRESHOLD;
    return {
      fieldKey,
      label: LEASE_FIELD_DEFS[fieldKey]?.label || fieldKey,
      value,
      confidence,
      ready,
    };
  });

  return {
    items,
    complete: items.every((item) => item.ready),
  };
}

export function runDeterministicLeaseExtraction({ rawText, formFields = {} }) {
  const pages = stripRepeatedHeadersAndFooters(splitIntoPages(rawText));
  const normalizedPages = pages.map((row) => ({
    page: Number(row.page || 0),
    text: normalizePageTextWithLineBreaks(row.text),
  }));

  const fields = {};
  Object.keys(LEASE_FIELD_DEFS).forEach((fieldKey) => {
    const proposal = collectFieldProposal({
      fieldKey,
      pages: normalizedPages,
      formFields,
    });
    fields[fieldKey] = {
      value: proposal.value,
      confidence: clampConfidence(proposal.confidence),
      evidence: Array.isArray(proposal.evidence) ? proposal.evidence.slice(0, 3) : [],
      source: "heuristic",
      rationale: proposal.rationale,
    };
  });

  const unresolvedFields = Object.keys(fields)
    .map((fieldKey) => {
      const row = fields[fieldKey] || {};
      const def = LEASE_FIELD_DEFS[fieldKey] || {};
      const confidence = clampConfidence(row.confidence);
      const lowConfidence = !safeString(row.value) || confidence < LEASE_CONFIDENCE_THRESHOLD;
      if (!lowConfidence) return null;
      const snippets = row.evidence && row.evidence.length
        ? row.evidence.slice(0, 3)
        : selectKeywordSnippets(normalizedPages, def.keywords || [], 3);
      return {
        fieldKey,
        constraints: def.constraints || "",
        currentValue: safeString(row.value),
        snippets: snippets.slice(0, 3),
      };
    })
    .filter(Boolean);

  return {
    extractionVersion: LEASE_EXTRACTION_VERSION,
    fields,
    unresolvedFields,
    requiredChecklist: buildLeaseRequiredChecklist(fields),
  };
}

export function buildLeaseLlmUnresolvedPayload(
  result,
  threshold = LEASE_CONFIDENCE_THRESHOLD,
  options = {}
) {
  if (typeof threshold === "object" && threshold) {
    options = threshold;
    threshold = LEASE_CONFIDENCE_THRESHOLD;
  }
  if (typeof options !== "object" || !options) options = {};

  const fields = result && result.fields ? result.fields : {};
  const unresolved = result && Array.isArray(result.unresolvedFields)
    ? result.unresolvedFields
    : [];
  const thresholdValue = Number.isFinite(Number(threshold)) ? Number(threshold) : LEASE_CONFIDENCE_THRESHOLD;
  const unresolvedByKey = new Map(
    unresolved
      .filter((row) => row && row.fieldKey)
      .map((row) => [row.fieldKey, row])
  );

  const includeFieldKeys = Array.isArray(options.includeFieldKeys) && options.includeFieldKeys.length
    ? options.includeFieldKeys
    : LEASE_REQUIRED_FIELD_KEYS;

  const unresolvedRequiredFieldKeys = includeFieldKeys.filter((fieldKey) => {
    const field = fields[fieldKey] || {};
    const value = safeString(field.value);
    return (
      !value ||
      clampConfidence(field.confidence) < thresholdValue ||
      !fieldValueLooksValid(fieldKey, value)
    );
  });

  if (!unresolvedRequiredFieldKeys.length) {
    return [];
  }

  return unresolvedRequiredFieldKeys.map((fieldKey) => {
    const row = unresolvedByKey.get(fieldKey) || {};
    const field = fields[fieldKey] || {};
    const fallbackEvidence = Array.isArray(field.evidence) ? field.evidence.slice(0, 3) : [];
    return {
      fieldKey,
      constraints: row.constraints || LEASE_FIELD_DEFS[fieldKey]?.constraints || "",
      currentValue: row.currentValue || safeString(field.value),
      snippets: Array.isArray(row.snippets) && row.snippets.length
        ? row.snippets.slice(0, 3)
        : fallbackEvidence,
    };
  });
}

export function mergeLeaseFieldProposals({ heuristicFields, llmFields }) {
  const base = heuristicFields && typeof heuristicFields === "object" ? heuristicFields : {};
  const llm = llmFields && typeof llmFields === "object" ? llmFields : {};
  const merged = {};

  const keys = new Set([...Object.keys(base), ...Object.keys(llm)]);
  keys.forEach((fieldKey) => {
    const heuristic = base[fieldKey] || {};
    const llmRow = llm[fieldKey] || null;
    if (!llmRow) {
      merged[fieldKey] = {
        ...heuristic,
        source: heuristic.source || "heuristic",
      };
      return;
    }

    const heuristicConfidence = clampConfidence(heuristic.confidence);
    const llmConfidence = clampConfidence(llmRow.confidence);
    const heuristicRationale = safeString(heuristic.rationale).toLowerCase();
    const heuristicFromFormField = heuristicRationale.includes("embedded pdf form field");
    const preferLlm = safeString(llmRow.value) && (
      llmConfidence >= heuristicConfidence ||
      (!heuristicFromFormField && heuristicConfidence < 0.93 && llmConfidence >= 0.7)
    );

    merged[fieldKey] = preferLlm
      ? {
          value: safeString(llmRow.value),
          confidence: llmConfidence,
          evidence: Array.isArray(llmRow.evidence) ? llmRow.evidence.slice(0, 3) : [],
          source: "llm",
          rationale: safeString(llmRow.rationale || "LLM assisted extraction."),
        }
      : {
          ...heuristic,
          source: heuristic.source || "heuristic",
        };
  });

  return merged;
}

export function selectLeasePageImagesForLlm(pageImages, unresolvedFields, options = {}) {
  const images = Array.isArray(pageImages) ? pageImages : [];
  if (!images.length) return [];

  const maxPages = Number.isFinite(Number(options.maxPages))
    ? Math.max(1, Number(options.maxPages))
    : 4;
  const defaultPages = Array.isArray(options.defaultPages) && options.defaultPages.length
    ? options.defaultPages
    : [1, 2];
  const requestedPages = new Set(
    defaultPages
      .map((n) => Number(n))
      .filter((n) => Number.isFinite(n) && n > 0)
  );

  const unresolved = Array.isArray(unresolvedFields) ? unresolvedFields : [];
  unresolved.forEach((field) => {
    const snippets = Array.isArray(field?.snippets) ? field.snippets : [];
    snippets.forEach((snippet) => {
      const page = Number(snippet?.page);
      if (Number.isFinite(page) && page > 0) requestedPages.add(page);
    });
  });

  const normalized = images
    .map((row) => ({
      page: Number.isFinite(Number(row?.page)) ? Number(row.page) : 0,
      dataUrl: safeString(row?.dataUrl),
    }))
    .filter((row) => row.page > 0 && row.dataUrl)
    .sort((a, b) => a.page - b.page);

  const selected = [];
  const selectedPageSet = new Set();
  normalized.forEach((row) => {
    if (!requestedPages.has(row.page)) return;
    if (selectedPageSet.has(row.page)) return;
    selected.push(row);
    selectedPageSet.add(row.page);
  });

  if (selected.length < maxPages) {
    normalized.forEach((row) => {
      if (selected.length >= maxPages) return;
      if (selectedPageSet.has(row.page)) return;
      selected.push(row);
      selectedPageSet.add(row.page);
    });
  }

  return selected.slice(0, maxPages);
}

export async function computeFileSha256(file) {
  if (!file) return "";
  const buffer = await file.arrayBuffer();
  const digest = await crypto.subtle.digest("SHA-256", buffer);
  const bytes = new Uint8Array(digest);
  return Array.from(bytes)
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

export function validateLeaseLlmResultShape(rawFields, requestedFieldKeys) {
  const requested = new Set((requestedFieldKeys || []).map((key) => safeString(key)).filter(Boolean));
  const out = {};
  const source = rawFields && typeof rawFields === "object" ? rawFields : {};

  Object.entries(source).forEach(([rawKey, rawValue]) => {
    const fieldKey = safeString(rawKey);
    if (!fieldKey || !requested.has(fieldKey)) return;
    const row = rawValue && typeof rawValue === "object" ? rawValue : {};
    const evidence = Array.isArray(row.evidence)
      ? row.evidence
          .map((item) => ({
            page: Number.isFinite(Number(item?.page)) ? Number(item.page) : 0,
            quote: truncate(item?.quote, 420),
          }))
          .filter((item) => item.quote)
          .slice(0, 3)
      : [];

    out[fieldKey] = {
      value: truncate(row.value, 300),
      confidence: clampConfidence(row.confidence),
      rationale: truncate(row.rationale, 220),
      evidence,
      source: "llm",
    };
  });

  return out;
}

export { selectKeywordSnippets };
